// Abonnement web push de l'appareil (AgDR-LOCAL-0001). La permission n'est
// demandée qu'après un geste (« Activer les rappels »), jamais au lancement :
// Safari l'exige. L'abonnement est renvoyé au serveur à chaque ouverture, pour
// qu'un abonnement renouvelé par le navigateur ne se perde pas. Le service worker
// le renvoie aussi seul (`pushsubscriptionchange`), avec la session écrite ici.
import { api, BASE_API } from './api';
import { jetonCourant } from './jeton';
import { ecrireSessionSw } from './session-sw';

export type EtatPush =
  'actif' | 'a-demander' | 'refuse' | 'installer-dabord' | 'non-pris-en-charge';

export function estInstallee(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** iPhone ou iPad (iPadOS se présente comme un Mac tactile). */
export function estIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function plateforme(): 'ios' | 'android' | 'autre' {
  if (estIos()) return 'ios';
  return navigator.userAgent.includes('Android') ? 'android' : 'autre';
}

function pushDisponible(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function etatPush(): EtatPush {
  // Sur iPhone, le web push n'existe que dans l'appli ajoutée à l'écran d'accueil (iOS 16.4+).
  if (estIos() && !estInstallee()) return 'installer-dabord';
  if (!pushDisponible()) return 'non-pris-en-charge';
  if (Notification.permission === 'granted') return 'actif';
  return Notification.permission === 'denied' ? 'refuse' : 'a-demander';
}

function cleVersOctets(base64url: string): Uint8Array<ArrayBuffer> {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const brut = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const octets = new Uint8Array(new ArrayBuffer(brut.length));
  for (let i = 0; i < brut.length; i++) octets[i] = brut.charCodeAt(i);
  return octets;
}

async function abonnementCourant(clePublique: string): Promise<PushSubscription> {
  const enregistrement = await navigator.serviceWorker.ready;
  const existant = await enregistrement.pushManager.getSubscription();
  if (existant) return existant;
  return enregistrement.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: cleVersOctets(clePublique),
  });
}

/** Envoie l'abonnement au serveur, puis donne au service worker de quoi le renouveler seul. */
async function envoyer(abonnement: PushSubscription): Promise<void> {
  const p = plateforme();
  await api.abonner(abonnement.toJSON(), p);
  const jeton = jetonCourant();
  if (jeton) await ecrireSessionSw({ jeton, plateforme: p, api: BASE_API }).catch(() => undefined);
}

/** Demande la permission (à appeler depuis un clic), abonne l'appareil et l'envoie au serveur. */
export async function activerRappels(clePublique: string): Promise<EtatPush> {
  if (!pushDisponible()) return 'non-pris-en-charge';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'refuse' : 'a-demander';
  await envoyer(await abonnementCourant(clePublique));
  return 'actif';
}

/** À chaque ouverture : renvoie l'abonnement si la permission est déjà accordée. */
export async function rafraichirAbonnement(clePublique: string): Promise<boolean> {
  if (etatPush() !== 'actif') return false;
  await envoyer(await abonnementCourant(clePublique));
  return true;
}

/** « Tout effacer » : l'appareil ne reçoit plus rien, même si le serveur est injoignable. */
export async function desabonnerAppareil(): Promise<string | null> {
  if (!('serviceWorker' in navigator)) return null;
  const enregistrement = await navigator.serviceWorker.getRegistration();
  const abonnement = await enregistrement?.pushManager.getSubscription();
  if (!abonnement) return null;
  const { endpoint } = abonnement;
  await abonnement.unsubscribe();
  return endpoint;
}
